import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { groupsApi } from '@/api/endpoints/groups';
import { cn } from '@/lib/utils';
import type { Group } from '@/api/types';
import { mensagemDeErro } from '@/lib/apiError';

export function ClientsPage() {
  // Sem `all=true`: o Traccar retorna 400 sem privilégio admin/manager.
  const list = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupsApi.list(),
  });
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter((g) => g.name?.toLowerCase().includes(q));
  }, [list.data, search]);

  const groupsById = useMemo(() => {
    const m = new Map<number, Group>();
    for (const g of list.data ?? []) m.set(g.id, g);
    return m;
  }, [list.data]);

  // Editor em TELA cheia (não janela) para caber toda a ficha.
  if (editing) {
    return (
      <ClientEditor
        initial={editing === 'new' ? null : editing}
        allGroups={list.data ?? []}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/settings"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            Agrupamentos de embarcações (clientes ou empresas). Permite hierarquia.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Novo cliente
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar cliente"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando clientes…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">
          Erro ao carregar clientes (precisa permissão de admin/manager).
        </p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Users2 className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search ? 'Nenhum cliente corresponde à busca.' : 'Nenhum cliente cadastrado.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <ClientCard
              key={g.id}
              group={g}
              parent={g.groupId ? groupsById.get(g.groupId)?.name : undefined}
              onEdit={() => setEditing(g)}
            />
          ))}
        </div>
      )}

    </div>
  );
}

function ClientCard({
  group,
  parent,
  onEdit,
}: {
  group: Group;
  parent?: string;
  onEdit: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Users2 className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{group.name}</p>
              {parent ? (
                <p className="truncate text-xs text-muted-foreground">Sob: {parent}</p>
              ) : (
                <p className="truncate text-xs text-muted-foreground">Cliente raiz</p>
              )}
              {(() => {
                const a = (group.attributes ?? {}) as Record<string, unknown>;
                const doc = typeof a.documento === 'string' ? a.documento : '';
                const tel = typeof a.telefone === 'string' ? a.telefone : '';
                const line = [doc, tel].filter(Boolean).join(' · ');
                return line ? <p className="truncate text-[11px] text-muted-foreground">{line}</p> : null;
              })()}
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

const CAD_KEYS = [
  'endereco', 'cidade', 'uf', 'pais', 'cep',
  'cod_interno', 'documento', 'cpf_cnpj', 'email', 'telefone', 'telefone_urgencia', 'responsavel',
  'valor_plano', 'tipo_cobranca', 'data_vencimento', 'obs_financeira', 'obs',
] as const;

type CadKey = (typeof CAD_KEYS)[number];

interface FormState extends Record<CadKey, string> {
  name: string;
  groupId: number | null;
}

/** Lê os campos da ficha de attributes do grupo. */
function cadFromGroup(g: Group | null): Record<CadKey, string> {
  const a = (g?.attributes ?? {}) as Record<string, unknown>;
  const s = (k: string) => (typeof a[k] === 'string' ? (a[k] as string) : '');
  return Object.fromEntries(CAD_KEYS.map((k) => [k, s(k)])) as Record<CadKey, string>;
}

function ClientEditor({
  initial,
  allGroups,
  onClose,
}: {
  initial: Group | null;
  allGroups: Group[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({
    name: initial?.name ?? '',
    groupId: initial?.groupId ?? null,
    ...cadFromGroup(initial),
  });
  const [touched, setTouched] = useState(false);
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (initial) setForm({ name: initial.name, groupId: initial.groupId ?? null, ...cadFromGroup(initial) });
  }, [initial]);

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  // Pra evitar ciclo: ao editar, não permitir selecionar a si mesmo nem descendentes como pai.
  const validParentOptions = useMemo(() => {
    if (!initial) return allGroups;
    const banned = new Set<number>([initial.id]);
    let added = true;
    while (added) {
      added = false;
      for (const g of allGroups) {
        if (g.groupId && banned.has(g.groupId) && !banned.has(g.id)) {
          banned.add(g.id);
          added = true;
        }
      }
    }
    return allGroups.filter((g) => !banned.has(g.id));
  }, [allGroups, initial]);

  const buildPayload = (): Group => {
    const cadAttrs = Object.fromEntries(CAD_KEYS.map((k) => [k, form[k].trim() || undefined]));
    return {
      ...(initial ?? { id: 0, name: '' }),
      name: form.name.trim(),
      groupId: form.groupId ?? undefined,
      attributes: { ...(initial?.attributes ?? {}), ...cadAttrs },
    } as Group;
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      if (initial) return groupsApi.update(initial.id, payload);
      const { id, ...rest } = payload;
      void id;
      return groupsApi.create(rest);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => groupsApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
  });

  const errorText = mensagemDeErro;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!initial) return;
    if (!window.confirm(`Excluir o cliente "${initial.name}"? Essa ação não pode ser desfeita.`)) return;
    deleteMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <button
        type="button"
        onClick={onClose}
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </button>
      <div>
        <h1 className="text-xl font-bold">{initial ? 'Editar cliente' : 'Novo cliente'}</h1>
        <p className="text-sm text-muted-foreground">Ficha cadastral do cliente (agrupamento de embarcações).</p>
      </div>
      <Card>
        <CardContent className="space-y-3 p-4">
          <Field label="Nome" required error={touched ? errors.name : undefined}>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </Field>
          <Field label="Cliente pai (opcional)">
            <select
              value={form.groupId === null ? '' : String(form.groupId)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  groupId: e.target.value ? Number(e.target.value) : null,
                }))
              }
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Nenhum (raiz)</option>
              {validParentOptions
                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </select>
          </Field>

          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-bold">Endereço</legend>
            <Field label="Endereço"><Input value={form.endereco} onChange={(e) => set('endereco', e.target.value)} placeholder="Logradouro, nº, bairro" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cidade"><Input value={form.cidade} onChange={(e) => set('cidade', e.target.value)} /></Field>
              <Field label="UF"><Input value={form.uf} maxLength={2} onChange={(e) => set('uf', e.target.value.toUpperCase())} placeholder="SP" /></Field>
              <Field label="País"><Input value={form.pais} onChange={(e) => set('pais', e.target.value)} placeholder="Brasil" /></Field>
              <Field label="CEP"><Input value={form.cep} onChange={(e) => set('cep', e.target.value)} placeholder="00000-000" /></Field>
            </div>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-bold">Dados</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cód. Interno"><Input value={form.cod_interno} onChange={(e) => set('cod_interno', e.target.value)} placeholder="Código do cliente" /></Field>
              <Field label="Documento"><Input value={form.documento} onChange={(e) => set('documento', e.target.value)} placeholder="RG / IE" /></Field>
              <Field label="CPF/CNPJ"><Input value={form.cpf_cnpj} onChange={(e) => set('cpf_cnpj', e.target.value)} placeholder="00.000.000/0000-00" /></Field>
              <Field label="Responsável"><Input value={form.responsavel} onChange={(e) => set('responsavel', e.target.value)} placeholder="Pessoa de contato" /></Field>
              <Field label="E-mail"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="contato@cliente.com" /></Field>
              <Field label="Tel. Contato"><Input value={form.telefone} onChange={(e) => set('telefone', e.target.value)} placeholder="(11) 9...." /></Field>
              <Field label="Tel. Urgência"><Input value={form.telefone_urgencia} onChange={(e) => set('telefone_urgencia', e.target.value)} placeholder="(11) 9...." /></Field>
            </div>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-bold">Financeiro</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Valor do Plano"><Input value={form.valor_plano} onChange={(e) => set('valor_plano', e.target.value)} placeholder="R$ 0,00" /></Field>
              <Field label="Tipo de Cobrança">
                <select value={form.tipo_cobranca} onChange={(e) => set('tipo_cobranca', e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                  <option value="">—</option>
                  <option value="boleto">Boleto</option>
                  <option value="pix">PIX</option>
                  <option value="cartao">Cartão</option>
                  <option value="transferencia">Transferência</option>
                  <option value="dinheiro">Dinheiro</option>
                </select>
              </Field>
              <Field label="Data Vencimento"><Input value={form.data_vencimento} onChange={(e) => set('data_vencimento', e.target.value)} placeholder="dia 10 ou 2026-01-10" /></Field>
            </div>
            <Field label="Observações Financeira"><Input value={form.obs_financeira} onChange={(e) => set('obs_financeira', e.target.value)} placeholder="Notas financeiras" /></Field>
            <Field label="Observações gerais"><Input value={form.obs} onChange={(e) => set('obs', e.target.value)} placeholder="Notas internas" /></Field>
          </fieldset>

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {initial && (
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={deleteMutation.isPending || saveMutation.isPending}
            className="mr-auto gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
          <Check className="h-4 w-4" />
          {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}
